import m from "mithril";
import toast from "../js/toaster";
import ExpiryStorage from "../js/expiryStorage";
import { clearCachedUserData } from "../utils.js";
import dataLayer from "../pwa/data-layer.js";

// Clear any potential overlays or modal states on login page load
window.addEventListener('DOMContentLoaded', () => {
    // Remove any lingering modal overlays
    const modals = document.querySelectorAll('.modal, .overlay, [class*="modal"], [class*="overlay"]');
    modals.forEach(modal => {
        modal.style.display = 'none';
        modal.remove();
    });

    // Clear any fixed position elements that might be blocking
    const blockers = document.querySelectorAll('[style*="position: fixed"], [style*="position: absolute"][style*="z-index"]');
    blockers.forEach(blocker => {
        // Only remove if it looks like an overlay (has high z-index and covers screen)
        const zIndex = parseInt(blocker.style.zIndex) || 0;
        if (zIndex > 1000) {
            blocker.style.display = 'none';
        }
    });

    // Ensure body has no blocking styles
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.zIndex = '';
});

const Login = {
    email: "",
    password: "",
    error: "",
    isLoading: false,
    rememberMe: false,
    showPassword: false, // Add showPassword field for password visibility toggle
    mfaToken: "",
    showMfaInput: false,

    oninit() {
        // Initialize theme from localStorage on component load
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }

        // Restore remember me preference
        const rememberMePreference = localStorage.getItem('rememberMe');
        this.rememberMe = rememberMePreference === 'true';

        // Pre-fill demo credentials if in development
        if (process.env.NODE_ENV === 'development') {
            this.email = "superuser@example.com"; // Updated to email for dev
            this.password = "super123";
        }
    },

    // Toggle dark mode
    toggleTheme() {
        const isDark = document.documentElement.classList.toggle('dark');
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
        m.redraw();
    },

    async onsubmit(e) {
        e.preventDefault();

        // Clear previous UI preference data (not authentication data)
        // Authentication is now handled by secure httpOnly cookies
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('user_id');
        localStorage.removeItem('subunit_id');
        localStorage.removeItem('subunit_name');
        localStorage.removeItem('subunit_assigned');

        sessionStorage.removeItem('username');
        sessionStorage.removeItem('role');
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('subunit_id');
        sessionStorage.removeItem('subunit_name');
        sessionStorage.removeItem('subunit_assigned');

        this.isLoading = true;
        this.error = "";
        m.redraw();

        try {
            console.log("🔐 Enterprise JWT + Refresh Token authentication");

            // Create timeout promise to prevent hanging requests
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
            });

            const fetchPromise = fetch('/api/secure-auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include', // Include cookies for httpOnly authentication
                body: JSON.stringify({
                    email: this.email,
                    password: this.password,
                    rememberMe: this.rememberMe,
                    mfaToken: this.mfaToken
                })
            });

            // Race between fetch and timeout
            const res = await Promise.race([fetchPromise, timeoutPromise]);

            console.log("🔐 Enterprise auth response status:", res.status);

            let data;
            try {
                data = await res.json();
                console.log("🔐 Enterprise auth response data:", data);
            } catch (parseError) {
                console.error("Failed to parse JSON response:", parseError);
                throw new Error("Invalid JSON response from enterprise auth endpoint");
            }

            // Always reset loading state
            this.isLoading = false;
            m.redraw();

            // Check if MFA is required
            if (res.ok && !data.success && data.requireMfa) {
                // Safeguard: Clear all possible overlays before showing MFA input
                // This prevents the "blank block" issue in Chrome
                this.forceRemoveOverlays();

                this.showMfaInput = true;
                this.error = "";
                // Focus MFA input on next tick
                setTimeout(() => {
                    const mfaInput = document.getElementById('mfa-token');
                    if (mfaInput) {
                        mfaInput.focus();
                        // Additional safeguard: ensure pointer events are enabled
                        document.body.style.pointerEvents = 'auto';
                    }
                }, 100);
                return;
            }

            // Check if authentication was successful
            if (res.ok && data.success && data.user) {
                console.log("🔐 Enterprise authentication successful, user data:", data.user);

                // Clear any existing cached user data first
                clearCachedUserData();

                // 🛡️ SECURITY AUDIT FIX: Check if the previous user was different OR missing
                // If the user has changed on this device OR if this is the first time the security
                // tracking is active, we MUST clear the IndexedDB cache to prevent data leaks 
                // between subunits from previous un-tracked sessions.
                const lastUserId = localStorage.getItem('last_user_id');
                const currentUserId = data.user.id.toString();
                
                if (!lastUserId || lastUserId !== currentUserId) {
                    console.log(`[Login] 🛡️ ${!lastUserId ? 'Baseline security' : 'User changed'} purge triggered. Clearing IndexedDB...`);
                    // Baseline: if lastUserId is missing, it means this device might have 
                    // stale data from pre-fix sessions. We clear once to be absolutely sure.
                    await dataLayer.clearAllData();
                    if (lastUserId) {
                        toast.info('Data offline untuk pengguna sebelumnya telah dihapus demi keamanan.');
                    }
                }
                
                // Update the last user ID
                localStorage.setItem('last_user_id', currentUserId);

                // With enterprise JWT + Refresh Token system:
                // - Authentication tokens are managed by httpOnly cookies (secure)
                // - User data comes from server response (validated)
                // - Remember Me controls refresh token expiration, not UI storage
                const user = data.user;

                // Store minimal user data for UI purposes (validated by server)
                // Remember Me now controls refresh token persistence, not localStorage
                const storeUserData = (key, value) => {
                    if (this.rememberMe) {
                        // Store in localStorage for persistent UI preferences
                        localStorage.setItem(key, value);
                    } else {
                        // Store in sessionStorage for temporary UI preferences
                        sessionStorage.setItem(key, value);
                    }
                };

                // Store non-sensitive user data for UI display only
                storeUserData('email', user.email);
                storeUserData('username', user.username);
                storeUserData('role', user.role);
                storeUserData('user_id', user.id.toString());
                storeUserData('passwordAgeOverdue', user.passwordAgeOverdue ? 'true' : 'false');
                storeUserData('mfa_enabled', user.mfa_enabled ? 'true' : 'false');

                // Handle subunit assignment from server response
                if (user.role === 'superuser') {
                    // Superuser has access to all business units
                    storeUserData('subunit_id', '');
                    storeUserData('subunit_name', 'Semua Unit Bisnis');
                    storeUserData('subunit_assigned', 'false');
                } else if (user.subunitId || user.subunit_id) {
                    // Use subunitId from server response
                    const subunitId = user.subunitId || user.subunit_id;
                    storeUserData('subunit_id', subunitId.toString());
                    storeUserData('subunit_name', 'Unit Bisnis'); // Default name
                    storeUserData('subunit_assigned', 'true');
                } else {
                    // User not assigned to any subunit
                    console.error(`❌ Login Error: User ${user.email} (ID: ${user.id}, Role: ${user.role}) has no subunit assignment. Keys:`, Object.keys(user));
                    this.error = `Akun ${user.email} (${user.role}) tidak terhubung dengan unit manapun. (ID: ${user.id})`;
                    m.redraw();
                    return;
                }

                // Store remember me preference for future login attempts
                // This affects refresh token expiration on server
                localStorage.setItem('rememberMe', this.rememberMe ? 'true' : 'false');

                // Store theme preference (always persistent for better UX)
                const currentTheme = localStorage.getItem('theme') || 'light';
                localStorage.setItem('theme', currentTheme);

                console.log("🔐 Enterprise login complete, session info:", data.session);

                // Initialize automatic token refresh
                initializeTokenRefresh();

                // Successful login - redirect based on device type and role
                const deviceInfo = window.deviceInfo || {};
                const isMobile = deviceInfo.isMobile;
                const isStandalone = deviceInfo.isStandalone;
                const userRole = user.role;

                if ((isMobile || isStandalone) && userRole === 'koordinator') {
                    console.log("🔐 Mobile or PWA koordinator detected - redirecting to mobile dashboard");
                    m.route.set('/coord-dashboard-mobile');
                } else if (userRole === 'kasir') {
                    console.log("🔐 Kasir user detected - redirecting to kasir dashboard");
                    m.route.set('/kasir-dashboard');
                } else if (userRole === 'akuntansi') {
                    console.log("🔐 Akuntansi user detected - redirecting to akuntansi dashboard");
                    m.route.set('/akuntansi-dashboard');
                } else {
                    console.log("🔐 Desktop or non-koordinator user - redirecting to regular dashboard");

                    m.route.set('/dashboard');
                }

            } else {
                // Handle authentication error
                console.log("🔐 Enterprise authentication failed:", data.error || "Invalid credentials");
                this.error = data.error || "Kredensial tidak valid. Silakan periksa kembali email dan password Anda.";
                m.redraw();
            }
        } catch (error) {
            // Handle network or other unexpected errors
            console.error('Enterprise login error:', error);

            // CRITICAL: Always reset loading state in catch block
            this.isLoading = false;

            // Additional overlay cleanup for safety
            this.forceRemoveOverlays();

            if (error.message === 'Request timeout') {
                this.error = "Permintaan timeout. Silakan periksa koneksi internet dan coba lagi.";
            } else {
                this.error = "Terjadi kesalahan koneksi. Silakan periksa koneksi internet Anda dan coba lagi.";
            }
            m.redraw();
        }
    },

    // Add overlay removal helper
    forceRemoveOverlays() {
        console.log('🧹 Force removing any overlay elements...');

        // Remove common overlay patterns
        const overlaySelectors = [
            '.modal', '.overlay', '[class*="modal"]', '[class*="overlay"]',
            '[class*="loading"]', '[class*="spinner"]', '[data-overlay]'
        ];

        overlaySelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
                const rect = el.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(el);
                const isVisible = rect.width > 0 && rect.height > 0 && computedStyle.display !== 'none';

                if (isVisible) {
                    console.log('🗑️ Removing overlay:', el);
                    el.style.display = 'none';
                    el.remove();
                }
            });
        });

        // Reset body styles that might be blocking
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.zIndex = '';
    },
    view() {
        const isDarkMode = document.documentElement.classList.contains('dark');

        return m("div", { class: "min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4" }, [
            m("div", { class: "fixed top-4 right-4 z-10" }, [
                m("button", {
                    class: "p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200",
                    onclick: () => this.toggleTheme(),
                    "aria-label": isDarkMode ? "Beralih ke mode terang" : "Beralih ke mode gelap"
                }, [
                    m("i", { class: isDarkMode ? "ri-sun-line text-xl" : "ri-moon-line text-xl" })
                ])
            ]),

            m("div", { class: "max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 space-y-6" }, [

                m("div", { class: "text-center" }, [
                    m("img", { src: "/img/login.png", alt: "Logo", class: "w-48 h-48 mx-auto mb-4" }),
                ]),
                // m("div", { class: "text-center" }, [
                //     m("div", { class: "mx-auto bg-orange-100 dark:bg-orange-900/30 w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors duration-200" }, [
                //         m("i", { class: "ri-bank-line text-3xl text-orange-600 dark:text-orange-400" })
                //     ]),
                //     m("h2", { class: "text-3xl font-bold text-gray-900 dark:text-white mb-2" }, "VITTA"),
                //     m("p", { class: "text-gray-600 dark:text-gray-300" }, "Simpan Pinjam"),
                //     m("p", { class: "text-gray-500 dark:text-gray-400 mt-4" }, "Masuk ke akun Anda untuk melanjutkan"),
                // ]),

                // Display error message if exists
                this.error ? m("div", { class: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 transition-colors duration-200" }, [
                    m("div", { class: "flex items-center text-red-700 dark:text-red-300" }, [
                        m("i", { class: "ri-error-warning-line mr-2" }),
                        m("span", this.error)
                    ])
                ]) : null,

                m("form", { onsubmit: (e) => this.onsubmit(e) }, [
                    // Email field
                    !this.showMfaInput ? m("div", [
                        m("label", { for: "email", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Alamat Email"),
                        m("div", { class: "relative" }, [
                            m("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                                m("i", { class: "ri-user-line text-gray-400 dark:text-gray-500" })
                            ]),
                            m("input", {
                                id: "email",
                                type: "email",
                                placeholder: "Masukkan alamat email",
                                class: "block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200",
                                value: this.email,
                                oninput: (e) => { this.email = e.target.value; },
                                disabled: this.isLoading,
                                autocomplete: "email"
                            })
                        ])
                    ]) : null,

                    // Password field with visibility toggle
                    !this.showMfaInput ? m("div", { class: "mt-4" }, [
                        m("label", { for: "password", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Kata Sandi"),
                        m("div", { class: "relative" }, [
                            m("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                                m("i", { class: "ri-lock-line text-gray-400 dark:text-gray-500" })
                            ]),
                            m("input", {
                                id: "password",
                                type: this.showPassword ? "text" : "password",
                                placeholder: "Masukkan kata sandi",
                                class: "block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200",
                                value: this.password,
                                oninput: (e) => { this.password = e.target.value; },
                                disabled: this.isLoading,
                                autocomplete: "current-password"
                            }),
                            m("button", {
                                type: "button",
                                class: "absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition-colors duration-200",
                                onclick: () => { this.showPassword = !this.showPassword; },
                                disabled: this.isLoading,
                                "aria-label": this.showPassword ? "Sembunyikan password" : "Tampilkan password"
                            }, [
                                m("i", { class: this.showPassword ? "ri-eye-off-line" : "ri-eye-line" })
                            ])
                        ])
                    ]) : null,

                    // MFA Input Field
                    this.showMfaInput ? m("div", { class: "mt-4" }, [
                        m("label", { for: "mfa-token", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Kode Autentikasi 2FA"),
                        m("div", { class: "relative" }, [
                            m("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                                m("i", { class: "ri-shield-keyhole-line text-gray-400 dark:text-gray-500" })
                            ]),
                            m("input", {
                                id: "mfa-token",
                                type: "text",
                                placeholder: "000000",
                                class: "block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 tracking-widest font-mono text-center text-lg",
                                value: this.mfaToken,
                                oninput: (e) => { this.mfaToken = e.target.value.replace(/[^0-9]/g, '').slice(0, 6); },
                                disabled: this.isLoading,
                                autocomplete: "one-time-code"
                            })
                        ])
                    ]) : null,

                    // Remember me and forgot password
                    !this.showMfaInput ? m("div", { class: "flex items-center justify-between mt-4" }, [
                        m("div", { class: "flex items-center" }, [
                            m("input", {
                                id: "remember-me",
                                type: "checkbox",
                                class: "h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded transition-colors duration-200",
                                checked: this.rememberMe,
                                onchange: (e) => { this.rememberMe = e.target.checked; },
                                disabled: this.isLoading
                            }),
                            m("label", {
                                for: "remember-me",
                                class: "ml-2 block text-sm text-gray-700 dark:text-gray-300 select-none"
                            }, "Ingat saya")
                        ]),
                        m("div", { class: "text-sm" }, [
                            m("a", {
                                href: "/forgot-password",
                                oncreate: m.route.link,
                                class: "font-medium text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 transition-colors duration-200"
                            }, "Lupa password?")
                        ])
                    ]) : null,

                    // Submit button
                    m("div", { class: "mt-6" }, [
                        m("button", {
                            type: "submit",
                            class: "w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 transition-all duration-200",
                            disabled: this.isLoading
                        }, [
                            this.isLoading ? [
                                m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                                "Memproses..."
                            ] : (this.showMfaInput ? "Verifikasi" : "Masuk")
                        ]),

                        this.showMfaInput ? m("button", {
                            type: "button",
                            class: "mt-3 w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200",
                            onclick: () => {
                                this.showMfaInput = false;
                                this.mfaToken = "";
                                this.password = ""; // Clear password for security
                            },
                            disabled: this.isLoading
                        }, "Kembali") : null
                    ])
                ]),

                // Demo credentials note
                process.env.NODE_ENV === 'development' ? m("div", { class: "mt-6 text-center text-sm text-gray-500 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg" }, [
                    m("p", "Demo: superuser/super123")
                ]) : null,

                // Footer
                m("div", { class: "mt-8 text-center text-xs text-gray-500 dark:text-gray-400" }, [
                    m("p", "© 2025 Vitta Simpan Pinjam. Semua hak dilindungi.")
                ])
            ])
        ]);
    }
};

/**
 * Initialize automatic token refresh system with sliding window approach
 * This function sets up continuous session extension until manual logout
 */
function initializeTokenRefresh() {
    // Get timeout duration based on remember me preference
    // Remember Me: 7 days, No Remember Me: 8 hours max (with 2-hour sliding refresh windows)
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const timeoutMinutes = rememberMe ? 10080 : 480; // 7 days or 8 hours
    console.log(`⏱️ Session timeout set to ${timeoutMinutes} minutes (${rememberMe ? '7 days' : '8 hours'})`);

    // Clear any existing refresh and inactivity intervals
    if (window.tokenRefreshInterval) {
        clearInterval(window.tokenRefreshInterval);
    }
    if (window.slidingRefreshInterval) {
        clearInterval(window.slidingRefreshInterval);
    }
    if (window.inactivityInterval) {
        clearInterval(window.inactivityInterval);
    }

    console.log('🔄 Initializing sliding window session extension...');

    // Sliding window session manager
    window.slidingRefreshManager = {
        isActive: true,
        lastSuccessfulRefresh: Date.now(),
        refreshAttempts: 0,
        maxAttempts: 50, // Safety limit to prevent infinite loops

        async refreshSession() {
            if (!this.isActive || this.refreshAttempts >= this.maxAttempts) {
                console.log('🛑 Sliding refresh stopped (max attempts reached or inactive)');
                return;
            }

            // Check user activity before attempting refresh
            const now = Date.now();
            const inactivityPeriod = (now - window.lastActivity) / (1000 * 60); // Convert to minutes

            if (inactivityPeriod > timeoutMinutes) {
                console.log(`🔒 Skipping refresh: User inactive for ${timeoutMinutes}+ minutes, will force logout soon`);
                return; // Don't extend session for inactive users
            }

            try {
                this.refreshAttempts++;
                console.log(`🔄 Sliding window refresh attempt ${this.refreshAttempts}... (User active for ${Math.round(inactivityPeriod)} minutes)`);

                const res = await fetch('/api/secure-auth/refresh', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({}), // Empty body - backend will read HttpOnly cookie
                    credentials: 'include'
                });

                if (res.ok) {
                    const data = await res.json();
                    this.lastSuccessfulRefresh = Date.now();
                    this.refreshAttempts = 0; // Reset attempts on success
                    console.log('✅ Sliding window refresh successful - session extended 15 more minutes');

                    // Store session extension info
                    if (window.sessionExtensionCount === undefined) {
                        window.sessionExtensionCount = 0;
                    }
                    window.sessionExtensionCount++;

                    // Show periodic notification (every 10 extensions)
                    if (window.sessionExtensionCount % 10 === 0) {
                        const hours = Math.floor(window.sessionExtensionCount * 0.25); // 15 min = 0.25 hours
                        toast.info(`Sesi Anda telah diperpanjang otomatis ${hours} jam total. Sesi akan terus diperpanjang hingga Anda keluar.`);
                    }
                } else {
                    console.warn('⚠️ Sliding refresh failed, will retry in 1 minute');
                    // Don't redirect to login - keep trying
                    setTimeout(() => this.refreshSession(), 60 * 1000); // Retry in 1 minute
                }
            } catch (error) {
                console.error('❌ Sliding refresh error:', error);
                // Retry after 1 minute for network errors
                setTimeout(() => this.refreshSession(), 60 * 1000);
            }
        },

        startSlidingRefresh() {
            // Initial immediate refresh - SKIPPED to avoid redundant 401s on fresh login
            // this.refreshSession();
            console.log('⏳ Sliding refresh: Disabled (using reactive auto-refresh instead)');

            // DISABLED: Background refresh can caus race conditions with navigation auto-refresh logic
            // We now rely on fetchWithAutoRefresh to handle token expiry reactively
            /*
            window.slidingRefreshInterval = setInterval(() => {
                this.refreshSession();
            }, 10 * 60 * 1000); // 10 minutes

            console.log('✅ Sliding window session manager started - will refresh every 10 minutes');
            */
        },

        stop() {
            this.isActive = false;
            clearInterval(window.slidingRefreshInterval);
            console.log('🛑 Sliding window session manager stopped');
        },

        restart() {
            this.isActive = true;
            this.refreshAttempts = 0;
            console.log('🔄 Sliding window session manager restarted');
            this.refreshSession();
        }
    };

    // Start the sliding refresh system
    window.slidingRefreshManager.startSlidingRefresh();

    // Track user activity to determine if we should keep the session alive
    window.lastActivity = Date.now();
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach(event => {
        document.addEventListener(event, () => {
            window.lastActivity = Date.now();
        }, true);
    });

    // Monitor activity and force logout after timeout period of inactivity
    window.inactivityInterval = setInterval(() => {
        const now = Date.now();
        const inactivityPeriod = (now - window.lastActivity) / (1000 * 60); // Convert to minutes

        if (inactivityPeriod > timeoutMinutes) {
            console.log(`🔒 Force logout: ${timeoutMinutes}+ minutes of inactivity detected`);
            toast.info(`Sesi berakhir karena tidak ada aktivitas selama ${rememberMe ? '7 hari' : '8 jam'}`);
            window.logoutUser();
        }
    }, 60 * 1000); // Check every minute

    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
        window.slidingRefreshManager?.stop();
        clearInterval(window.tokenRefreshInterval);
    });

    console.log('🔄 Sliding window session extension with force logout initialized');
}

/**
 * Manual logout function with session cleanup
 */
window.logoutUser = async function () {
    // Prevent multiple concurrent logout attempts
    if (window.isLoggingOut) {
        console.log('⏳ Logout already in progress, skipping...');
        return;
    }

    try {
        window.isLoggingOut = true;
        console.log('🔐 Logging out user...');

        // Clear inactivity monitor immediately to prevent re-triggering during network call
        if (window.inactivityInterval) {
            clearInterval(window.inactivityInterval);
            window.inactivityInterval = null;
        }

        const res = await fetch('/api/secure-auth/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({}) // Explicitly send empty body to avoid compatibility issues
        });

        if (res.ok) {
            console.log('✅ Logout successful');
        }
    } catch (error) {
        console.error('❌ Logout error:', error);
    } finally {
        // Clear local client data regardless of server response
        // 🛡️ Preserve last_user_id and UI settings
        const lastUserId = localStorage.getItem('last_user_id');
        const theme = localStorage.getItem('theme');
        const rememberMePref = localStorage.getItem('rememberMe');
        
        localStorage.clear();
        sessionStorage.clear();
        
        // Restore preserved keys
        if (lastUserId) localStorage.setItem('last_user_id', lastUserId);
        if (theme) localStorage.setItem('theme', theme);
        if (rememberMePref) localStorage.setItem('rememberMe', rememberMePref);

        // Clear refresh intervals
        if (window.tokenRefreshInterval) {
            clearInterval(window.tokenRefreshInterval);
            window.tokenRefreshInterval = null;
        }
        if (window.slidingRefreshInterval) {
            clearInterval(window.slidingRefreshInterval);
            window.slidingRefreshInterval = null;
        }

        // Reset activity state tracker
        window.lastActivity = Date.now();
        window.isLoggingOut = false;

        // Redirect to login
        m.route.set('/login');
    }
};

/**
 * Get active user sessions (for session management UI)
 */
window.getActiveSessions = async function () {
    try {
        const res = await fetch('/api/auth/sessions', {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            },
            credentials: 'include'
        });

        if (res.ok) {
            const data = await res.json();
            return data.sessions || [];
        } else {
            console.error('Failed to get active sessions');
            return [];
        }
    } catch (error) {
        console.error('Error getting active sessions:', error);
        return [];
    }
};

/**
 * Revoke a specific session
 */
window.revokeSession = async function (sessionId) {
    try {
        const res = await fetch(`/api/auth/sessions/${sessionId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json'
            },
            credentials: 'include'
        });

        if (res.ok) {
            const data = await res.json();
            console.log('✅ Session revoked:', data.message);
            return true;
        } else {
            console.error('Failed to revoke session');
            return false;
        }
    } catch (error) {
        console.error('Error revoking session:', error);
        return false;
    }
};

export default Login;